import { useState, useEffect, useMemo, useCallback } from 'react';
import {
  useTriggerFormInit,
  useNodeDropdownLabels,
  buildShipmentFilterUpdate,
} from '../shared';
import { Form, Flex, Divider } from 'antd';
import {
  ActionTaken,
  type AlertActionTakenTriggerData,
} from '@/proto/logiblocks/logiblocks';
import useToken from '@/common/helper/useToken';
import { usePaginatedFilters } from '@/common/helper/usePaginatedFilters';
import { ALERT_TRIGGER_FILTER_CONFIGS } from '@/common/helper/filterConfigs';
import { CustomizationComponent } from '@/pages/Logiblocks/types';
import { getApplyConfig } from '@/pages/Settings/Notifications/NotificationConfig';
import { useFetchAlertThresholds, useFetchCompanyCouriers } from '@/hooks';
import { ShipmentFilterApply } from '../alertTrigger/components';
import { AddSelectableItem, CustomShipmentText } from '@/components';
import CheckboxMultiSelect, {
  type CheckboxMultiSelectGroup,
  type CheckboxMultiSelectShortcut,
} from '@/components/CheckboxMultiSelect';
import ShipmentAlertThresholds from '@/pages/Shipments/PerShipmentNotification/components/ShipmentAlertThresholds';
import { getTeamsDDLOFFeature } from '@/common/utils';
import { type DropdownLabelItem } from '@/apis/logiblocksLabelsAPI';
import { ShipmentFilterFormData } from '../nodeFormTypes';
import { FieldErrorLabel } from '../shared/FieldErrorLabel';
import { AlertActionTakenTriggerFields } from './AlertActionTakenTriggerNodeInfo';

interface AlertActionTakenTriggerFormData extends ShipmentFilterFormData {
  alertTypes?: string[];
}

const ACTION_TAKEN_GROUPS: CheckboxMultiSelectGroup[] = [
  {
    label: 'Manual actions',
    options: [
      {
        value: ActionTaken.ACTION_TAKEN_MANUAL_RESOLVED,
        label: 'Resolved',
        tagLabel: 'Manual resolved',
      },
      {
        value: ActionTaken.ACTION_TAKEN_MANUAL_DISMISSED,
        label: 'Dismissed',
        tagLabel: 'Manual dismissed',
      },
      {
        value: ActionTaken.ACTION_TAKEN_MANUAL_ASSIGNED,
        label: 'Assigned',
        tagLabel: 'Manual assigned',
      },
    ],
  },
  {
    label: 'AI actions',
    options: [
      {
        value: ActionTaken.ACTION_TAKEN_AI_RESOLVED,
        label: 'Resolved',
        tagLabel: 'AI resolved',
      },
      {
        value: ActionTaken.ACTION_TAKEN_AI_DISMISSED,
        label: 'Dismissed',
        tagLabel: 'AI dismissed',
      },
    ],
  },
  {
    label: 'System actions',
    options: [
      {
        value: ActionTaken.ACTION_TAKEN_SYSTEM_DISMISSED,
        label: 'Dismissed',
        tagLabel: 'System dismissed',
      },
      {
        value: ActionTaken.ACTION_TAKEN_SYSTEM_ASSIGNED,
        label: 'Assigned',
        tagLabel: 'System assigned',
      },
    ],
  },
];

// Each shortcut covers the same row label across every group, so a new
// group/row is picked up automatically.
const actionTakenValuesLabeled = (label: string): ActionTaken[] =>
  ACTION_TAKEN_GROUPS.flatMap((group) =>
    group.options.filter((option) => option.label === label)
  ).map((option) => option.value as ActionTaken);
const ACTION_TAKEN_SHORTCUTS: CheckboxMultiSelectShortcut[] = [
  { label: 'All resolved', values: actionTakenValuesLabeled('Resolved') },
  { label: 'All dismissed', values: actionTakenValuesLabeled('Dismissed') },
  { label: 'All assigned', values: actionTakenValuesLabeled('Assigned') },
];
const ACTION_TAKEN_OPTION_COUNT = ACTION_TAKEN_GROUPS.reduce(
  (count, group) => count + group.options.length,
  0
);

/**
 * Customization UI for ALERT_ACTION_TAKEN_TRIGGER nodes.
 */
export const AlertActionTakenTriggerCustomization: CustomizationComponent = ({
  nodeData,
  onChange,
  isSidePannelOpen,
  step,
  setStep,
  childAddSelectedTypesRef,
  nodeId,
  workflowId,
  isReadOnly = false,
  validationErrors,
}) => {
  const alertTypesError = validationErrors?.find(
    (e) => e.field === AlertActionTakenTriggerFields.ALERT_TYPES
  );
  const actionTakenError = validationErrors?.find(
    (e) => e.field === AlertActionTakenTriggerFields.ACTION_TAKEN
  );

  const { alertThreshold, isLoading } = useFetchAlertThresholds(null);
  const [selectAllAlertTypes, setSelectAllAlertTypes] = useState(false);
  const [selectedThresholdValues, setSelectedThresholdValues] = useState({});
  const [form] = Form.useForm();
  const { getToken } = useToken();
  const [teamsList, setTeamsList] = useState([]);

  // Pre-loaded labels for paginated dropdowns (fetched once when panel opens).
  const preloadedLabels = useNodeDropdownLabels({
    isSidePannelOpen,
    workflowId,
    nodeId,
    getToken,
  });

  useEffect(() => {
    getTeamsDDLOFFeature('', 'Shipment').then((val) => {
      if (val && val.length > 0) setTeamsList(val);
    });
  }, [setTeamsList]);

  // Memoize filter configs to prevent re-renders
  const filterConfigs = useMemo(() => ALERT_TRIGGER_FILTER_CONFIGS, []);

  // Use generic paginated filters hook
  const {
    getFilterData,
    getFilterLoading,
    getFilterHasMore,
    getFilterLoadMore,
  } = usePaginatedFilters(filterConfigs, getToken);

  const handleNodeData = (
    data: AlertActionTakenTriggerFormData,
    isSelectAll?: boolean
  ) => {
    if (!data || Object.keys(data).length === 0) return;
    onChange({
      alert_action_taken_trigger: buildShipmentFilterUpdate(
        data,
        nodeData.alert_action_taken_trigger,
        applyType,
        undefined,
        isSelectAll
      ),
    });
  };

  const handleAddSelectedTypes = useCallback(() => {
    let keys: string[] = [];
    if (Object.keys(selectedThresholdValues)?.length > 0) {
      keys = Object.keys(selectedThresholdValues);
    }
    const alertActionTakenTrigger: AlertActionTakenTriggerData = {
      ...nodeData.alert_action_taken_trigger,
      alert_types: keys,
      select_all_alert_types: selectAllAlertTypes,
    };
    onChange({ alert_action_taken_trigger: alertActionTakenTrigger });
    setStep(2);
  }, [
    selectedThresholdValues,
    nodeData.alert_action_taken_trigger,
    onChange,
    setStep,
    selectAllAlertTypes,
  ]);

  // Set up the ref for the parent to call handleAddSelectedTypes
  useEffect(() => {
    if (childAddSelectedTypesRef) {
      childAddSelectedTypesRef.current = handleAddSelectedTypes;
    }
  }, [childAddSelectedTypesRef, handleAddSelectedTypes]);

  const [updateAlertData, setUpdateAlertData] = useState(false);
  // When delete is triggered at step 2, persist the updated selection immediately
  useEffect(() => {
    if (updateAlertData) {
      handleAddSelectedTypes();
      setUpdateAlertData(false);
    }
  }, [updateAlertData, handleAddSelectedTypes]);

  const [applyType, setApplyType] = useState([]);
  const { couriers } = useFetchCompanyCouriers();

  /**
   * Merge helper: prepend pre-loaded label items that are not already present
   * in the paginated page-1 data.  Page data takes precedence (comes first so
   * the selector deduplication logic keeps the richer version).
   */
  const mergeWithPreloaded = useCallback(
    (pageData: unknown[], preloaded: DropdownLabelItem[]): unknown[] => {
      if (preloaded.length === 0) return pageData;
      const pageIds = new Set(
        (pageData as Array<{ id?: string | number }>).map((item) =>
          String(item.id ?? '')
        )
      );
      const extra = preloaded.filter((item) => !pageIds.has(String(item.id)));
      return [...pageData, ...extra];
    },
    []
  );

  // Memoize paginated data, enriched with pre-loaded labels for off-page values.
  const memoizedOriginsData = useMemo(
    () => mergeWithPreloaded(getFilterData(3), preloadedLabels.origins),
    [getFilterData, mergeWithPreloaded, preloadedLabels.origins]
  );
  const memoizedProductsData = useMemo(
    () => mergeWithPreloaded(getFilterData(1), preloadedLabels.products),
    [getFilterData, mergeWithPreloaded, preloadedLabels.products]
  );
  const memoizedDestinationsData = useMemo(
    () => mergeWithPreloaded(getFilterData(4), preloadedLabels.destinations),
    [getFilterData, mergeWithPreloaded, preloadedLabels.destinations]
  );

  const applyConfig = useMemo(
    () =>
      getApplyConfig({
        teamsList,
        origins: memoizedOriginsData,
        products: memoizedProductsData,
        couriers,
        destinations: memoizedDestinationsData,
      }),
    [
      teamsList,
      memoizedOriginsData,
      memoizedProductsData,
      couriers,
      memoizedDestinationsData,
    ]
  );

  // Initialize applyType and form fields from saved nodeData.
  const hasPatchedRef = useTriggerFormInit({
    isSidePannelOpen,
    triggerData: nodeData?.alert_action_taken_trigger,
    form,
    setApplyType,
    // Wait until alertThreshold data has loaded before patching.
    extraGuard: Boolean(alertThreshold && alertThreshold.length > 0),
    onPatched: (data) => {
      // Build a map of alertType → threshold_value from the loaded thresholds.
      const alertTypesArray = Array.isArray(data.alert_types)
        ? data.alert_types
        : [];
      const alertTypesObject: Record<string, string | number> = {};
      alertTypesArray.forEach((alertType: string) => {
        alertThreshold?.forEach((threshold) => {
          threshold?.alerts?.forEach((alert) => {
            if (alert.alert_type === alertType) {
              alertTypesObject[alertType] = alert.threshold_value;
            }
          });
        });
      });
      setSelectAllAlertTypes(data?.select_all_alert_types);
      setSelectedThresholdValues(alertTypesObject);
    },
  });

  // Reset the form-init patch flag on panel close so re-opening re-initializes.
  useEffect(() => {
    if (!isSidePannelOpen) {
      hasPatchedRef.current = false;
    }
  }, [isSidePannelOpen, hasPatchedRef]);

  const handleActionTakenChange = (next: Array<string | number>) => {
    const actionTaken = next as ActionTaken[];
    onChange({
      alert_action_taken_trigger: {
        ...nodeData.alert_action_taken_trigger,
        action_taken: actionTaken,
        select_all_action_taken:
          actionTaken.length === ACTION_TAKEN_OPTION_COUNT,
      },
    });
  };

  return (
    <>
      <Form form={form} layout='vertical'>
        <Flex vertical gap={16}>
          <Flex vertical>
            <ShipmentAlertThresholds
              shipment={null}
              selectAllAlertTypes={selectAllAlertTypes}
              setSelectAllAlertTypes={setSelectAllAlertTypes}
              step={step}
              alertThreshold={alertThreshold}
              setSelectedThresholdValues={setSelectedThresholdValues}
              selectedThresholdValues={selectedThresholdValues}
              maxHeight={'100%'}
              showThresholdInputSection={false}
              enableAllThresholds={true}
              setUpdateAlertData={setUpdateAlertData}
              isLoading={isLoading}
              disabled={isReadOnly}
            />
            {step === 2 && (
              <AddSelectableItem
                text='Select alert'
                onClick={() => setStep(3)}
              />
            )}
            <FieldErrorLabel error={alertTypesError} />
          </Flex>
          {step === 2 && (
            <>
              <Divider
                className={'notification-divider'}
                style={{ marginBottom: '4px' }}
              />
              <ShipmentFilterApply
                applyType={applyType}
                setApplyType={setApplyType}
                form={form}
                applyConfig={applyConfig}
                handleNodeUpdate={handleNodeData}
                isReadOnly={isReadOnly}
                origins={memoizedOriginsData}
                originsLoading={getFilterLoading(3)}
                originsHasMore={getFilterHasMore(3)}
                onLoadMoreOrigins={getFilterLoadMore(3)}
                products={memoizedProductsData}
                productsLoading={getFilterLoading(1)}
                productsHasMore={getFilterHasMore(1)}
                onLoadMoreProducts={getFilterLoadMore(1)}
                destinations={memoizedDestinationsData}
                destinationsLoading={getFilterLoading(4)}
                destinationsHasMore={getFilterHasMore(4)}
                onLoadMoreDestinations={getFilterLoadMore(4)}
              />
              <Flex vertical gap={0}>
                <CustomShipmentText
                  text='Action taken'
                  fontSize='14px'
                  marginBottom='4px'
                  fontWeight={600}
                />
                <CheckboxMultiSelect
                  groups={ACTION_TAKEN_GROUPS}
                  selectAllLabel='All actions'
                  shortcuts={ACTION_TAKEN_SHORTCUTS}
                  // Mounted inside the edit panel (not document.body) so
                  // scrolling past the end of the list scrolls the panel.
                  getPopupContainer={(trigger) =>
                    trigger.parentElement ?? document.body
                  }
                  value={nodeData?.alert_action_taken_trigger?.action_taken}
                  onChange={handleActionTakenChange}
                  disabled={isReadOnly}
                  className='agentSelect agentSelect--darkText'
                  placeholder='Select...'
                  border='0px'
                  background='rgba(94, 94, 94, 1)'
                  color='white'
                  showTagCloseIcon
                />
                <FieldErrorLabel error={actionTakenError} />
              </Flex>
            </>
          )}
        </Flex>
      </Form>
    </>
  );
};
